import React, { useEffect, useMemo, useState } from 'react';
import { Button, Dropdown, Icon, Label, Loader, Message, Table } from 'semantic-ui-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  API,
  copy,
  readAccessTokenPreference,
  saveAccessTokenPreference,
  showError,
  showSuccess,
} from '../../helpers';
import { renderQuota } from '../../helpers/render';
import './Access.css';

function normalizeBearerKey(value) {
  const key = (value || '').trim();
  if (!key) {
    return '';
  }
  return key.startsWith('sk-') ? key : `sk-${key}`;
}

function canUseTokenKey(token) {
  return Boolean(token?.key) && !token?.key_obscured;
}

function tokenAuthorization(token) {
  if (!canUseTokenKey(token)) {
    return '';
  }
  return normalizeBearerKey(token.key);
}

function tokenDisplayValue(token) {
  const authorization = tokenAuthorization(token);
  if (authorization) {
    return authorization;
  }
  const preview = (token?.key_preview || '').trim();
  if (preview && !preview.startsWith('kh_') && !preview.startsWith('sk-kh_')) {
    return normalizeBearerKey(preview);
  }
  if (token?.key_obscured) {
    return 'sk-******';
  }
  if (preview) {
    return normalizeBearerKey(preview);
  }
  return '';
}

function maskToken(value) {
  if (!value) {
    return '暂无可用令牌';
  }
  if (value.length <= 18) {
    return value;
  }
  return `${value.slice(0, 10)}...${value.slice(-6)}`;
}

function modalityLabel(modality) {
  const map = {
    chat: '语言模型',
    realtime: '实时对话',
    image: '图像',
    video: '视频',
    embedding: '向量',
    audio: '音频',
  };
  return map[modality] || modality || '语言模型';
}

function providerLabel(provider) {
  return provider || 'sinoaw';
}

function tokenAllowedModels(token) {
  const raw = token?.models || '';
  if (!raw.trim()) {
    return [];
  }
  return raw
    .split(',')
    .map((model) => model.trim())
    .filter(Boolean);
}

function applyTokenCatalogScope(catalog, token) {
  const allowedModels = tokenAllowedModels(token);
  if (!allowedModels.length) {
    return catalog;
  }
  const allowedSet = new Set(allowedModels);
  const scopedCatalog = catalog.filter((model) => allowedSet.has(model.name));
  if (scopedCatalog.length) {
    return scopedCatalog;
  }
  return allowedModels.map((name) => ({
    name,
    display_name: name,
    provider: '',
    modality: 'chat',
    ratio: 0,
    completion_ratio: 0,
    enabled_channel_count: 0,
    channel_count: 0,
  }));
}

function tokenStatusText(token) {
  switch (token?.status) {
    case 1:
      return '可用';
    case 2:
      return '已禁用';
    case 3:
      return '已过期';
    case 4:
      return '已用尽';
    default:
      return '未知状态';
  }
}

const Access = () => {
  const { t } = useTranslation();
  const [tokens, setTokens] = useState([]);
  const [models, setModels] = useState([]);
  const [user, setUser] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedTokenId, setSelectedTokenId] = useState(0);
  const [savedTokenId, setSavedTokenId] = useState(
    () => readAccessTokenPreference()?.id || 0
  );

  const apiBase = `${window.location.origin}/v1`;
  const defaultToken = useMemo(
    () => tokens.find((token) => token.status === 1) || tokens[0] || null,
    [tokens]
  );
  const activeToken = useMemo(() => {
    if (!tokens.length) {
      return null;
    }
    return tokens.find((token) => token.id === selectedTokenId) || defaultToken;
  }, [tokens, defaultToken, selectedTokenId]);
  const authorization = tokenAuthorization(activeToken);
  const displayToken = tokenDisplayValue(activeToken);
  const scopedModels = useMemo(
    () => applyTokenCatalogScope(models, activeToken),
    [models, activeToken]
  );
  const defaultModel = scopedModels[0]?.name || '请先开放可用模型';
  const tokenOptions = useMemo(
    () =>
      tokens.map((token) => ({
        key: token.id,
        value: token.id,
        text: token.name || `令牌 #${token.id}`,
        description:
          token.resource_package_id > 0
            ? `资源包 · ${tokenStatusText(token)}`
            : `账户余额 · ${tokenStatusText(token)}`,
      })),
    [tokens]
  );

  const curlSnippet = useMemo(() => {
    return `curl ${apiBase}/chat/completions \\
  -H "Authorization: Bearer ${authorization || 'sk-你的API令牌'}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "${defaultModel}",
    "messages": [{"role": "user", "content": "你好，介绍一下你自己"}],
    "stream": false
  }'`;
  }, [apiBase, authorization, defaultModel]);

  const nodeSnippet = useMemo(() => {
    return `import OpenAI from "openai";

const client = new OpenAI({
  apiKey: "${authorization || 'sk-你的API令牌'}",
  baseURL: "${apiBase}"
});

const response = await client.chat.completions.create({
  model: "${defaultModel}",
  messages: [{ role: "user", content: "你好，介绍一下你自己" }]
});

console.log(response.choices[0].message.content);`;
  }, [apiBase, authorization, defaultModel]);

  const loadAccessData = async () => {
    setLoading(true);
    try {
      const [tokenRes, modelRes, userRes] = await Promise.all([
        API.get('/api/token/?p=0&order='),
        API.get('/api/user/model_catalog'),
        API.get('/api/user/self'),
      ]);
      if (tokenRes.data.success) {
        const tokenList = tokenRes.data.data || [];
        setTokens(tokenList);
        setSelectedTokenId((current) => {
          if (tokenList.some((token) => token.id === current)) {
            return current;
          }
          const savedPreference = readAccessTokenPreference();
          if (tokenList.some((token) => token.id === savedPreference?.id)) {
            return savedPreference.id;
          }
          const defaultToken = tokenList.find((token) => token.status === 1) || tokenList[0];
          return defaultToken?.id || 0;
        });
      } else {
        showError(tokenRes.data.message);
      }
      if (modelRes.data.success) {
        setModels(modelRes.data.data || []);
      } else {
        showError(modelRes.data.message);
      }
      if (userRes.data.success) {
        setUser(userRes.data.data || {});
      }
    } catch (error) {
      showError(error);
    } finally {
      setLoading(false);
    }
  };

  const saveSelectedToken = () => {
    if (!activeToken) {
      showError('请先创建 API 令牌');
      return;
    }
    if (activeToken.status !== 1) {
      showError('只能保存可用状态的 API 令牌');
      return;
    }
    const saved = saveAccessTokenPreference(activeToken);
    setSavedTokenId(saved?.id || 0);
    showSuccess('已保存为体验中心默认令牌');
  };

  useEffect(() => {
    loadAccessData();
  }, []);

  const copyText = async (text, message) => {
    if (await copy(text)) {
      showSuccess(message);
    } else {
      showError('复制失败，请手动复制');
    }
  };

  if (loading) {
    return (
      <div className='access-loading'>
        <Loader active inline='centered' />
      </div>
    );
  }

  return (
    <div className='access-shell'>
      <section className='access-hero'>
        <div>
          <Label className='access-eyebrow'>API 接入中心</Label>
          <h2>统一模型调用入口</h2>
          <p>余额、令牌、模型权限和示例代码集中在这里，开发接入可以从这一页开始。</p>
        </div>
        <div className='access-balance'>
          <span>账户余额</span>
          <strong>{renderQuota(user.quota || 0, t)}</strong>
          <Button as={Link} to='/topup' size='small' primary>
            充值
          </Button>
        </div>
      </section>

      <section className='access-grid'>
        <article className='access-panel access-token-panel'>
          <div className='access-panel-head'>
            <div>
              <h3>API 令牌</h3>
              <p>{activeToken ? activeToken.name || '默认令牌' : '还没有可用令牌'}</p>
            </div>
            <div className='access-token-actions'>
              <Button
                basic={savedTokenId !== activeToken?.id}
                color={savedTokenId === activeToken?.id ? 'green' : undefined}
                icon
                labelPosition='left'
                disabled={!activeToken || activeToken.status !== 1}
                onClick={saveSelectedToken}
              >
                <Icon name='save' />
                {savedTokenId === activeToken?.id ? '已保存' : '保存'}
              </Button>
              <Button as={Link} to='/token' basic icon labelPosition='left'>
                <Icon name='key' />
                管理
              </Button>
            </div>
          </div>
          {tokens.length > 1 && (
            <Dropdown
              selection
              fluid
              className='access-token-select'
              value={activeToken?.id || 0}
              options={tokenOptions}
              onChange={(_, { value }) => setSelectedTokenId(Number(value))}
            />
          )}
          {activeToken && (
            <div className='access-token-meta'>
              <Label basic color={activeToken.status === 1 ? 'green' : 'grey'}>
                {tokenStatusText(activeToken)}
              </Label>
              <Label basic color={activeToken.resource_package_id > 0 ? 'teal' : 'blue'}>
                {activeToken.resource_package_id > 0 ? '资源包扣量' : '账户余额扣费'}
              </Label>
              {savedTokenId === activeToken.id && (
                <Label basic color='green'>
                  体验中心默认
                </Label>
              )}
              {!authorization && (
                <span>令牌明文已隐藏，仅展示预览</span>
              )}
            </div>
          )}
          <div className='access-token-box'>
            <code>{maskToken(displayToken)}</code>
            <Button
              icon
              primary
              aria-label='复制令牌'
              onClick={() => copyText(authorization, 'API 令牌已复制')}
              disabled={!authorization}
            >
              <Icon name='copy' />
            </Button>
          </div>
          {!activeToken && (
            <Message info>
              <Message.Header>需要先创建 API 令牌</Message.Header>
              <p>创建后即可通过 OpenAI 兼容 SDK 调用模型。</p>
            </Message>
          )}
          {activeToken && !authorization && (
            <Message info>
              <Message.Header>当前令牌明文不可复制</Message.Header>
              <p>令牌明文仅创建时展示；如需复制完整密钥，请到 API 令牌页新建令牌。</p>
            </Message>
          )}
        </article>

        <article className='access-panel access-endpoint-panel'>
          <div className='access-panel-head'>
            <div>
              <h3>调用地址</h3>
              <p>OpenAI 兼容协议</p>
            </div>
            <Button
              basic
              icon
              aria-label='复制调用地址'
              onClick={() => copyText(apiBase, '调用地址已复制')}
            >
              <Icon name='copy outline' />
            </Button>
          </div>
          <div className='access-endpoint'>
            <span>Base URL</span>
            <code>{apiBase}</code>
          </div>
          <div className='access-status-list'>
            <Label color={scopedModels.length > 0 ? 'green' : 'orange'}>
              {scopedModels.length > 0 ? `${scopedModels.length} 个可用模型` : '暂无可用模型'}
            </Label>
            <Label color={authorization ? 'blue' : activeToken ? 'grey' : 'grey'}>
              {authorization ? '令牌可复制' : activeToken ? '令牌已隐藏' : '等待创建令牌'}
            </Label>
          </div>
        </article>
      </section>

      <section className='access-panel'>
        <div className='access-panel-head'>
          <div>
            <h3>可调用模型</h3>
            <p>模型权限来自 API 令牌和后台模型管理配置。</p>
          </div>
          <Button basic icon labelPosition='left' onClick={loadAccessData}>
            <Icon name='refresh' />
            刷新
          </Button>
        </div>
        <Table basic='very' className='access-model-table'>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>模型</Table.HeaderCell>
              <Table.HeaderCell>分类</Table.HeaderCell>
              <Table.HeaderCell>供应商</Table.HeaderCell>
              <Table.HeaderCell>倍率</Table.HeaderCell>
              <Table.HeaderCell>通道</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {scopedModels.map((item) => (
              <Table.Row key={item.name}>
                <Table.Cell>
                  <strong>{item.display_name || item.name}</strong>
                  <span>{item.name}</span>
                </Table.Cell>
                <Table.Cell>{modalityLabel(item.modality)}</Table.Cell>
                <Table.Cell>{providerLabel(item.provider)}</Table.Cell>
                <Table.Cell>
                  <Label basic>
                    输入 {Number(item.ratio || 0).toFixed(4)}
                  </Label>
                  <Label basic>
                    输出 {Number(item.completion_ratio || 0).toFixed(4)}
                  </Label>
                </Table.Cell>
                <Table.Cell>
                  <Label color={item.enabled_channel_count > 0 || item.api_enabled ? 'green' : 'orange'}>
                    {item.api_enabled
                      ? '专属 API'
                      : `${item.enabled_channel_count || 0}/${item.channel_count || 0}`}
                  </Label>
                </Table.Cell>
              </Table.Row>
            ))}
            {scopedModels.length === 0 && (
              <Table.Row>
                <Table.Cell colSpan='5' textAlign='center'>
                  当前令牌暂无可用模型
                </Table.Cell>
              </Table.Row>
            )}
          </Table.Body>
        </Table>
      </section>

      <section className='access-code-grid'>
        <article className='access-panel'>
          <div className='access-panel-head'>
            <div>
              <h3>curl</h3>
              <p>服务端验证最直接</p>
            </div>
            <Button basic icon onClick={() => copyText(curlSnippet, 'curl 示例已复制')}>
              <Icon name='copy outline' />
            </Button>
          </div>
          <pre>{curlSnippet}</pre>
        </article>

        <article className='access-panel'>
          <div className='access-panel-head'>
            <div>
              <h3>Node.js SDK</h3>
              <p>OpenAI 兼容客户端</p>
            </div>
            <Button basic icon onClick={() => copyText(nodeSnippet, 'Node.js 示例已复制')}>
              <Icon name='copy outline' />
            </Button>
          </div>
          <pre>{nodeSnippet}</pre>
        </article>
      </section>

      <section className='access-error-grid'>
        {[
          ['401', '令牌无效或未携带 Authorization'],
          ['403', '模型未授权或账号被冻结'],
          ['429', '请求过快或上游限流'],
          ['5xx', '上游通道异常，可稍后重试'],
        ].map(([code, text]) => (
          <div className='access-error-item' key={code}>
            <strong>{code}</strong>
            <span>{text}</span>
          </div>
        ))}
      </section>
    </div>
  );
};

export default Access;
